import { X, GripVertical, Languages, Type, Hash, Ellipsis, Maximize2, Minimize2, HelpCircle } from 'lucide-react';
import type { KeyboardLayer, KeyboardMode } from './keyboard.types';

interface KeyboardToolbarProps {
  language: string;
  layer: KeyboardLayer;
  mode?: KeyboardMode;
  onLayerChange: (layer: KeyboardLayer) => void;
  onModeToggle?: () => void;
  onHelpClick?: () => void;
  onClose: () => void;
  t?: any;
}

const LAYER_OPTIONS: {
  key: KeyboardLayer;
  label: string;
  Icon: typeof Hash;
  shortLabel: string;
}[] = [
  { key: 'african', label: 'African', Icon: Languages, shortLabel: 'afr' },
  { key: 'default', label: 'Basic', Icon: Type, shortLabel: 'abc' },
  { key: 'punctuation', label: 'Symbols', Icon: Hash, shortLabel: '123' },
  { key: 'diacritics', label: 'Accents', Icon: Ellipsis, shortLabel: 'áéí' },
];

function getBaseLayer(layer: KeyboardLayer): KeyboardLayer {
  if (layer === 'uppercase') return 'default';
  if (layer === 'africanUpper') return 'african';
  return layer;
}

export function KeyboardToolbar({
  language,
  layer,
  mode,
  onLayerChange,
  onModeToggle,
  onHelpClick,
  onClose,
  t,
}: KeyboardToolbarProps) {
  const baseLayer = getBaseLayer(layer);
  const toolbarText = t?.keyboard?.toolbar || {};

  return (
    <div className="flex items-center justify-between gap-3 border-b border-white/[0.08] px-4 py-2.5 text-white">
      <div className="flex min-w-0 items-center gap-2">
        <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-white/28 active:cursor-grabbing" />
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[10px] font-semibold uppercase tracking-[0.24em] text-brand">
            {language}
          </span>
          <div className="h-3 w-px bg-white/12" />
          <span className="text-[9px] font-medium uppercase tracking-wider text-white/45">
            Premium
          </span>
        </div>
      </div>

      <div className="flex min-w-0 items-center gap-3">
        <div className="relative flex min-w-0 items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.035] px-1.5 py-1 shadow-[inset_0_-1px_0_rgba(255,255,255,0.03)]">
          {LAYER_OPTIONS.map(({ key, label, Icon, shortLabel }) => {
            const isActive = baseLayer === key;

            return (
              <button
                key={key}
                type="button"
                aria-pressed={isActive}
                aria-label={label}
                onClick={() => onLayerChange(key)}
                className={`group relative flex h-6 items-center gap-1.5 rounded px-2 text-[10px] font-semibold uppercase tracking-[0.08em] transition-all duration-200 ${
                  isActive
                    ? 'bg-brand/[0.13] text-brand shadow-[inset_0_-1px_0_rgba(251,146,60,0.35)]'
                    : 'text-white/50 hover:bg-white/[0.055] hover:text-white/82'
                }`}
              >
                <Icon
                  className={`h-2.5 w-2.5 transition-all duration-200 ${
                    isActive ? 'text-brand' : 'text-white/38 group-hover:text-white/70'
                  }`}
                />
                <span className="hidden sm:inline">{shortLabel}</span>
              </button>
            );
          })}
        </div>

        {onModeToggle && (
          <button
            type="button"
            aria-label={mode === 'compact' ? toolbarText.expandMode || 'Switch to extended mode' : toolbarText.compactMode || 'Switch to compact mode'}
            onClick={onModeToggle}
            className="group flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/[0.10] bg-white/[0.035] text-white/48 transition-all duration-200 hover:border-brand/35 hover:bg-brand/[0.10] hover:text-brand"
            title={mode === 'compact' ? toolbarText.expandMode || 'Show all characters' : toolbarText.compactMode || 'Compact view'}
          >
            {mode === 'compact' ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
          </button>
        )}

        {onHelpClick && (
          <button
            type="button"
            aria-label={toolbarText.help || 'Keyboard help'}
            onClick={onHelpClick}
            className="group flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/[0.10] bg-white/[0.035] text-white/48 transition-all duration-200 hover:border-info/35 hover:bg-info/[0.10] hover:text-info"
            title={toolbarText.help || 'View keyboard guide'}
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        )}

        <button
          type="button"
          aria-label={toolbarText.close || 'Close keyboard'}
          onClick={onClose}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/[0.10] bg-white/[0.03] text-white/38 transition-all duration-200 hover:border-danger/25 hover:bg-danger/[0.08] hover:text-danger"
          title={toolbarText.close || 'Close keyboard'}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
